import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

/**
 * Normalizes a raw reminder from the API into the shape used by the frontend.
 * The API returns `files` as an array of { uid, url } objects; we keep them as-is
 * but also expose a flat `fileUids` array for convenience.
 */
const normalizeReminder = (reminder) => ({
    ...reminder,
    fileUids: (reminder.files ?? []).map((f) => f.uid),
});

export const useReminders = () => {
    return useQuery({
        queryKey: ['reminders'],
        queryFn: async () => {
            const reminders = await api.getReminders();
            return reminders.map(normalizeReminder);
        },
    });
};

export const useCreateReminder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ data }) => api.createReminder(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reminders'] });
        },
    });
};

export const useUpdateReminder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reminderUid, data }) => api.updateReminder(reminderUid, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reminders'] });
        },
    });
};

export const useDeleteReminder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reminderUid }) => api.deleteReminder(reminderUid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reminders'] });
        },
    });
};

export const useAdvanceReminder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ reminderUid }) => api.advanceReminder(reminderUid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reminders'] });
        },
    });
};
